/**
 * ReviewPage — 復習モード (FR-08 / UNC-02)
 * 3連続正解で自動マスター表示（手動「外す/残す」ダイアログは作らない: UNC-02確定）
 * 復習問題は /review-items/{id}/question から取得し、解答は通常演習と同じ /questions/{id}/answer を使用
 */

import { useEffect, useState, useContext, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { listReviewItems, getReviewQuestion, submitAnswer } from '../api'
import { ApiClientError } from '../api/client'
import type { ReviewItem, QuestionForPractice, AnswerResult, CertificationType } from '../api/types'
import { AuthContext } from '../contexts/AuthContext'
import SourceBlock from '../components/SourceBlock'
import Icon from '../components/Icon'
import Spinner, { PageLoader } from '../components/Spinner'

export default function ReviewPage() {
  const { certType } = useContext(AuthContext)
  const navigate = useNavigate()
  const [items, setItems] = useState<ReviewItem[]>([])
  const [currentItemIndex, setCurrentItemIndex] = useState(0)
  const [question, setQuestion] = useState<QuestionForPractice | null>(null)
  const [selected, setSelected] = useState<number | null>(null)
  const [result, setResult] = useState<AnswerResult | null>(null)
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [started, setStarted] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!certType) return
    listReviewItems(certType as CertificationType)
      .then((res) => setItems(res?.review_items ?? []))
      .finally(() => setLoading(false))
  }, [certType])

  const loadQuestion = useCallback(async (item: ReviewItem) => {
    if (!item.id) return
    setQuestion(null)
    setSelected(null)
    setResult(null)
    setError(null)
    try {
      const q = await getReviewQuestion(item.id)
      setQuestion(q)
    } catch (err) {
      if (err instanceof ApiClientError) setError(err.message)
      else setError('問題の取得に失敗しました')
    }
  }, [])

  const handleStart = async () => {
    setStarted(true)
    if (items.length > 0) await loadQuestion(items[0])
  }

  const handleSubmit = async () => {
    if (!question?.id || selected == null) return
    setSubmitting(true)
    setError(null)
    try {
      const r = await submitAnswer(question.id, {
        user_answer: selected,
        answered_at: new Date().toISOString(),
      })
      setResult(r)
      // 復習リストの items を再取得して consecutive_correct を反映
      if (certType) {
        listReviewItems(certType as CertificationType).then((res) => setItems(res?.review_items ?? []))
      }
    } catch (err) {
      if (err instanceof ApiClientError) setError(err.message)
      else setError('解答の送信に失敗しました')
    } finally {
      setSubmitting(false)
    }
  }

  const handleNext = async () => {
    const nextIndex = currentItemIndex + 1
    if (nextIndex >= items.length) {
      // 全問完了
      setStarted(false)
      setCurrentItemIndex(0)
      setQuestion(null)
      setResult(null)
      if (certType) {
        listReviewItems(certType as CertificationType).then((res) => setItems(res?.review_items ?? []))
      }
      return
    }
    setCurrentItemIndex(nextIndex)
    await loadQuestion(items[nextIndex])
  }

  const currentItem = items[currentItemIndex]
  const activeItems = items.filter((i) => i.is_active)

  if (loading) return <PageLoader />

  // 復習リスト表示
  if (!started) {
    return (
      <div className="mx-auto max-w-content p-screen">
        <h1 className="mb-1 text-[22px] font-bold tracking-tight">復習</h1>

        {activeItems.length === 0 ? (
          <div className="mt-6 rounded-lg border border-border bg-surface px-6 py-12 text-center">
            <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-correct-bg text-correct">
              <Icon name="award" size={26} aria-hidden={false} />
            </div>
            <div className="text-base font-semibold text-text-primary">復習する問題はありません</div>
            <div className="mt-1 text-sm text-text-muted">演習で間違えた問題が復習リストに追加されます</div>
            <button onClick={() => navigate('/practice')} className="btn-primary mt-5 px-5">
              演習を始める
            </button>
          </div>
        ) : (
          <>
            <p className="mb-4 text-sm text-text-secondary">
              復習リスト（{activeItems.length}問）
              <span className="ml-2 text-xs text-text-muted">3連続正解でマスター</span>
            </p>

            <div data-testid="review-list" className="mb-5 overflow-hidden rounded-lg border border-border bg-surface">
              {activeItems.map((item, idx) => (
                <div
                  key={item.id}
                  data-testid="review-list-item"
                  className={[
                    'flex items-center gap-2.5 px-4 py-3.5',
                    idx < activeItems.length - 1 ? 'border-b border-border' : '',
                  ].join(' ')}
                >
                  {/* ring-dot (#63) */}
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--status-review-color)" strokeWidth="2" aria-hidden="true" className="flex-none">
                    <circle cx="12" cy="12" r="9" />
                    <circle cx="12" cy="12" r="3" fill="var(--status-review-color)" stroke="none" />
                  </svg>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm text-text-primary">{item.question_text}</div>
                    <div className="mt-0.5 text-xs text-text-muted">
                      {item.category_name_ja} / 連続正解 {item.consecutive_correct ?? 0}/3
                    </div>
                  </div>
                  <span className="rounded-pill bg-review-bg px-2 py-0.5 text-xs font-semibold text-review">要復習</span>
                </div>
              ))}
            </div>

            <button data-testid="review-start" onClick={handleStart} className="btn-primary w-full">
              復習を始める
            </button>
          </>
        )}
      </div>
    )
  }

  // 復習出題画面
  return (
    <div className="mx-auto max-w-content p-screen">
      <div className="mb-4 flex items-center justify-between">
        <div className="text-sm text-text-secondary">
          復習 {currentItemIndex + 1} / {items.length}問
        </div>
        <button
          onClick={() => {
            setStarted(false)
            setCurrentItemIndex(0)
          }}
          className="flex items-center gap-1 text-[13px] text-text-secondary transition-colors hover:text-text-primary"
        >
          <Icon name="x-circle" size={16} /> 中断
        </button>
      </div>

      {error && (
        <div role="alert" className="alert-error mb-4">
          {error}
        </div>
      )}

      {question ? (
        <>
          {/* カテゴリ + 連続正解 */}
          <div className="mb-1.5 text-xs text-text-muted">
            {question.category_name_ja}
            {currentItem && <span className="ml-2">連続正解 {currentItem.consecutive_correct ?? 0}/3</span>}
          </div>

          <div data-testid="question-text" className="mb-5 text-base font-medium leading-body">
            {question.question_text}
          </div>

          <div data-testid="choice-list" className="mb-5 flex flex-col gap-2">
            {question.choices?.map((c) => {
              const isAnswered = result != null
              let boxCls = 'border border-border-strong bg-surface'
              let badgeCls = 'bg-surface-alt text-text-secondary'
              let tag: React.ReactNode = null

              if (isAnswered) {
                if (c.choice_no === result.correct_choice) {
                  boxCls = 'border-2 border-correct bg-correct-bg'
                  tag = (
                    <span className="flex items-center gap-1 text-xs font-bold text-correct">
                      <Icon name="check" size={14} aria-hidden={false} /> 正解
                    </span>
                  )
                } else if (c.choice_no === result.user_answer && c.choice_no !== result.correct_choice) {
                  boxCls = 'border-2 border-incorrect bg-incorrect-bg'
                  tag = (
                    <span className="flex items-center gap-1 text-xs font-bold text-incorrect">
                      <Icon name="x" size={14} aria-hidden={false} /> あなたの解答
                    </span>
                  )
                }
              } else if (selected === c.choice_no) {
                boxCls = 'border-2 border-accent bg-accent-subtle'
                badgeCls = 'bg-accent text-white'
              }

              return (
                <button
                  key={c.choice_no}
                  data-testid="choice-option"
                  onClick={() => !isAnswered && setSelected(c.choice_no ?? null)}
                  disabled={isAnswered || submitting}
                  aria-pressed={selected === c.choice_no && !isAnswered}
                  className={`flex min-h-[52px] w-full items-center gap-3 rounded-md px-3.5 py-3 text-left text-[15px] text-text-primary transition-colors duration-fast ${boxCls} ${isAnswered || submitting ? 'cursor-default' : 'cursor-pointer'}`}
                >
                  <span className={`flex h-6 w-6 flex-none items-center justify-center rounded text-[13px] font-bold ${badgeCls}`}>
                    {c.choice_no}
                  </span>
                  <span className="flex-1">{c.choice_text}</span>
                  {tag}
                </button>
              )
            })}
          </div>

          {result && (
            <>
              {/* マスター通知 (UNC-02: 3連続正解) */}
              {result.is_correct && (currentItem?.consecutive_correct ?? 0) + 1 >= 3 && (
                <div className="mb-3 flex items-center justify-center gap-2 rounded-md border border-correct bg-correct-bg px-3.5 py-2.5 text-sm font-semibold text-correct-on">
                  <Icon name="award" size={16} aria-hidden={false} />
                  3連続正解！復習リストからマスターしました
                </div>
              )}

              <div
                data-testid="result-banner"
                className={[
                  'mb-4 flex items-center gap-2 rounded-md border px-3.5 py-3 text-base font-bold',
                  result.is_correct
                    ? 'border-correct bg-correct-bg text-correct-on'
                    : 'border-incorrect bg-incorrect-bg text-incorrect-on',
                ].join(' ')}
              >
                <Icon name={result.is_correct ? 'check' : 'x'} size={18} aria-hidden={false} />
                {result.is_correct ? '正解！' : '不正解'}
              </div>

              <div data-testid="explanation" className="card mb-1 px-4 py-3.5">
                <div className="mb-2 text-[13px] font-bold text-text-secondary">解説</div>
                <div className="text-[15px] leading-body">{result.explanation}</div>
              </div>

              <SourceBlock
                sourceLaw={result.source_law}
                sourceReference={result.source_reference}
                confirmedDate={result.confirmed_date}
              />
            </>
          )}

          {!result ? (
            <button
              data-testid="answer-submit"
              onClick={handleSubmit}
              disabled={selected == null || submitting}
              className="btn-primary mt-2 w-full"
            >
              {submitting ? <Spinner label="送信中..." /> : '解答する'}
            </button>
          ) : (
            <button data-testid="next-question" onClick={handleNext} className="btn-primary mt-4 w-full">
              {currentItemIndex + 1 >= items.length ? '復習を終了する' : '次の問題へ'}
            </button>
          )}
        </>
      ) : (
        <PageLoader />
      )}
    </div>
  )
}
